/**
 * System Admin API client — Cycle 6 §4.
 * SYSTEM_ADMIN 의 multi-tenant 관리 endpoint 호출.
 */

import { apiClient } from './client';

// Cycle 73 — 'pending_approval'(셀프가입 후 SYSTEM_ADMIN 승인 대기) 추가. 'pending'(가입 진행 중,
// finalize 전)과 별개 transit-state. backend 가 두 값을 모두 직렬화하므로 frontend 전수 동기화 필수
// (cycle 72 antipattern-new-transit-status-omitted-from-existing-invariant-gates 의 frontend 인스턴스).
export type TenantStatus = 'active' | 'pending' | 'pending_approval' | 'suspended' | 'archived' | 'frozen';

export interface TenantListItem {
  id: string;
  code: string;
  name: string;
  domain: string | null;
  status: TenantStatus;
  verifiedAt: string | null;
  verificationMethod: string | null;
  graceUntil: string | null;
  graceActive: boolean;
  frozenAt: string | null;
  frozen: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TenantListResponse {
  items: TenantListItem[];
  nextCursor: string | null;
}

export interface TenantDetailResponse {
  tenant: TenantListItem & { allowedTransitions: TenantStatus[] };
  counts: { users: number; llmIntegrations: number };
  recentAudits: Array<{ id: string; eventType: string; ts: string; actorId: string | null }>;
}

export async function listTenants(opts: { status?: TenantStatus; cursor?: string; limit?: number } = {}): Promise<TenantListResponse> {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.limit) params.set('limit', String(opts.limit));
  const { data } = await apiClient.get<TenantListResponse>(`/system/tenants?${params.toString()}`);
  // 방어 가드(additive) — items 가 배열이 아닌 비정형 응답에도 소비처 map 크래시 차단.
  return { ...data, items: Array.isArray(data?.items) ? data.items : [] };
}

export async function getTenantDetail(id: string): Promise<TenantDetailResponse> {
  const { data } = await apiClient.get<TenantDetailResponse>(`/system/tenants/${id}`);
  return data;
}

export async function changeTenantStatus(id: string, payload: { status: 'active' | 'suspended' | 'archived'; reason: string }): Promise<{
  tenantId: string;
  previousStatus: string;
  newStatus: string;
  changedAt: string;
}> {
  const { data } = await apiClient.patch(`/system/tenants/${id}/status`, payload);
  return data;
}

// Cycle 73 — 회사 가입 승인/거부 (PATCH /status 와 별도 라우트 — backend 승인 게이트 우회 차단 의도
// 를 frontend 도 보존). approve 는 body 없음, reject 는 reason(≥10) 필수.
export async function approveOnboarding(id: string): Promise<{ tenantId: string; status: string; graceUntil?: string }> {
  const { data } = await apiClient.post(`/system/onboarding/${id}/approve`);
  return data;
}

export async function rejectOnboarding(id: string, payload: { reason: string }): Promise<{ tenantId: string; status: string }> {
  const { data } = await apiClient.post(`/system/onboarding/${id}/reject`, payload);
  return data;
}

// Cycle 7 — unfreeze
export async function unfreezeTenant(id: string, payload: { reason: string; evidenceUrl?: string }): Promise<{
  institutionId: string;
  status: string;
  unfrozenAt: string;
}> {
  const { data } = await apiClient.post(`/internal/institutions/${id}/unfreeze`, payload);
  return data;
}

// Cycle 7 — quota
export interface TenantQuota {
  tenantId: string;
  rateLimitPerMin: number | null;
  llmMaxTokensPerDay: string | null;  // BigInt as string
}

export async function getTenantQuota(id: string): Promise<TenantQuota> {
  const { data } = await apiClient.get<TenantQuota>(`/system/tenants/${id}/quota`);
  return data;
}

export async function updateTenantQuota(
  id: string,
  payload: { rateLimitPerMin?: number | null; llmMaxTokensPerDay?: number | null },
): Promise<TenantQuota> {
  const { data } = await apiClient.patch<TenantQuota>(`/system/tenants/${id}/quota`, payload);
  return data;
}

// Cycle 10 — audit dashboard
export interface AdminOverrideStats {
  windowDays: number;
  totalEvents: number;
  byAction: Record<string, number>;
  bySeriesDay: Array<{ day: string; count: number }>;
  recent: Array<{ ts: string; action: string; institutionId: string; actorId: string | null }>;
}

export async function getAdminOverrideStats(opts: { days?: number; institutionId?: string } = {}): Promise<AdminOverrideStats> {
  const params = new URLSearchParams();
  if (opts.days) params.set('days', String(opts.days));
  if (opts.institutionId) params.set('institutionId', opts.institutionId);
  const { data } = await apiClient.get<AdminOverrideStats>(`/system/audit/admin-overrides?${params.toString()}`);
  return data;
}

// Cycle 11 — LLM token usage dashboard
export interface LlmUsageItem {
  institutionId: string;
  name: string;
  domain: string | null;
  usedToday: number;
  limit: string | null;  // BigInt as string
  utilizationPct: number | null;
}

export interface LlmUsageResponse {
  date: string;
  totalTenants: number;
  totalTokensUsed: number;
  overQuotaCount: number;
  items: LlmUsageItem[];
}

export async function getLlmUsage(): Promise<LlmUsageResponse> {
  const { data } = await apiClient.get<LlmUsageResponse>('/system/llm-usage');
  return data;
}

// Cycle 20 — ai-gateway /health/config proxy
export interface AiGatewayConfig {
  safe: Record<string, string | null>;
  secret: Record<string, { set: boolean; len?: number; prefix4?: string }>;
  partial: Record<string, string | null>;
  validation: {
    production: boolean;
    passed: boolean;
    issues: string[];
  };
  ts: string;
}

export async function getAiGatewayConfig(): Promise<AiGatewayConfig> {
  const { data } = await apiClient.get<AiGatewayConfig>('/system/ai-gateway-config');
  return data;
}

// Cycle 23 — drift opt-out overview (SYSTEM_ADMIN dashboard)
export interface DriftInstitutionDisabled {
  institutionId: string;
  name: string;
  domain: string | null;
}
export interface DriftUserOptOutAgg {
  institutionId: string;
  name: string;
  count: number;
  latestUpdatedAt: string | null;
}
export interface DriftOptOutOverview {
  institutionsDisabled: DriftInstitutionDisabled[];
  userOptOutsByInstitution: DriftUserOptOutAgg[];
  totalInstitutionsDisabled: number;
  totalUsersOptedOut: number;
}

export async function getDriftOptOutOverview(): Promise<DriftOptOutOverview> {
  const { data } = await apiClient.get<DriftOptOutOverview>('/system/drift-opt-outs');
  return data;
}

// Cycle 24 — drift opt-out 변경 이력
export interface DriftOptOutHistoryItem {
  id: string;
  scope: 'user' | 'institution';
  userId: string | null;
  institutionId: string;
  institutionName: string;
  actorId: string;
  action: 'opt_out' | 'opt_in' | 'inst_opt_out' | 'inst_opt_in';
  reasonMasked: string | null;
  piiDetected: boolean;
  createdAt: string;
}

export async function getDriftOptOutHistory(opts: { institutionId?: string; limit?: number } = {}): Promise<{ items: DriftOptOutHistoryItem[]; limit: number }> {
  const params = new URLSearchParams();
  if (opts.institutionId) params.set('institutionId', opts.institutionId);
  if (opts.limit) params.set('limit', String(opts.limit));
  const { data } = await apiClient.get<{ items: DriftOptOutHistoryItem[]; limit: number }>(`/system/drift-opt-outs/history?${params.toString()}`);
  return { ...data, items: Array.isArray(data?.items) ? data.items : [] };
}

// Cycle 26 — 일자별 + action 별 분포 (chart 용)
export interface DriftOptOutDistDay {
  day: string;
  opt_out: number;
  opt_in: number;
  inst_opt_out: number;
  inst_opt_in: number;
}

export async function getDriftOptOutDistribution(opts: { days?: number } = {}): Promise<{ days: number; series: DriftOptOutDistDay[] }> {
  const params = new URLSearchParams();
  if (opts.days) params.set('days', String(opts.days));
  const { data } = await apiClient.get<{ days: number; series: DriftOptOutDistDay[] }>(`/system/drift-opt-outs/distribution?${params.toString()}`);
  return { ...data, series: Array.isArray(data?.series) ? data.series : [] };
}
